import { useState } from 'react';
import { Sparkles } from 'lucide-react';
import type { OptimizationReport } from '@monitoring-panel/types';
import { Skeleton } from '@/components/Skeleton';
import { usePersistentState } from '@/hooks/usePersistentState';
import { useOptimizationReport, useOptimizationReports } from '@/hooks/useOptimizationReports';
import { cn } from '@/lib/utils';
import { AppliedHistoryTab } from './AppliedHistoryTab';
import { LinesTab } from './LinesTab';
import { ReportDatePicker } from './ReportDatePicker';
import { ReviewTab } from './ReviewTab';
import { SuggestionsTab } from './SuggestionsTab';
import { SummaryTab } from './SummaryTab';
import { dateTime, fullDate } from './format';
import { asDeliveryReport } from './types';
import { dv360Context } from './links';

interface OptimizationsSectionProps {
  executionId: string;
}

const TABS = [
  { id: 'summary', label: 'Resumen' },
  { id: 'suggestions', label: 'Sugerencias' },
  { id: 'lines', label: 'Líneas' },
  { id: 'review', label: 'Puntos a revisar' },
  { id: 'history', label: 'Historial aplicado' },
] as const;

type TabId = (typeof TABS)[number]['id'];

const TAB_IDS: readonly string[] = TABS.map((tab) => tab.id);

/**
 * Daily optimization reports of the delivery-report skill for one execution:
 * pick a saved report, read its analysis and approve or reject its suggestions.
 */
export function OptimizationsSection({ executionId }: OptimizationsSectionProps) {
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [activeTab, setActiveTab] = usePersistentState<TabId>(
    'optimizations-tab',
    'summary',
    (value) => typeof value === 'string' && TAB_IDS.includes(value)
  );

  const reportsQuery = useOptimizationReports(executionId);
  const reports = reportsQuery.data?.reports ?? [];
  const reportDate = selectedDate ?? reports[0]?.reportDate ?? null;
  const reportQuery = useOptimizationReport(executionId, reportDate);

  if (reportsQuery.isLoading) {
    return <SectionSkeleton />;
  }

  if (reportsQuery.error) {
    return (
      <section className="space-y-3">
        <h2 className="adv-section-title">Optimizaciones</h2>
        <div className="adv-alert">No se pudieron cargar los informes: {reportsQuery.error.message}</div>
      </section>
    );
  }

  if (!reportDate) {
    return <EmptyState executionId={executionId} />;
  }

  const report = reportQuery.data?.report;

  return (
    <section className="space-y-4">
      <div className="adv-card adv-card--featured px-5 pt-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="adv-section-title">Optimizaciones</h2>
            {report && <ReportMeta report={report} />}
          </div>
          <ReportDatePicker reports={reports} selectedDate={reportDate} onSelect={setSelectedDate} />
        </div>
        {report && <p className="adv-section-desc mt-2">{report.summary}</p>}

        <nav className="adv-tabs mt-4 border-b-0" role="tablist">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn('adv-tab', activeTab === tab.id && 'adv-tab--active')}
            >
              {tab.label}
              {report && <TabCount tabId={tab.id} report={report} />}
            </button>
          ))}
        </nav>
      </div>

      {reportQuery.isLoading ? (
        <TabSkeleton />
      ) : reportQuery.error || !report ? (
        <div className="adv-alert">
          No se pudo cargar el informe del {fullDate(reportDate)}: {reportQuery.error?.message ?? 'sin datos'}
        </div>
      ) : (
        <TabContent tabId={activeTab} executionId={executionId} report={report} />
      )}
    </section>
  );
}

function ReportMeta({ report }: { report: OptimizationReport }) {
  return (
    <p className="text-adv-body-s text-adv-tertiary mt-1">
      {report.authorName ?? report.authorEmail} ·{' '}
      <span className="adv-num">{dateTime(report.generatedAt)}</span> ·{' '}
      <span className="adv-num">{report.applied}</span> aplicadas,{' '}
      <span className="adv-num">{report.pending}</span> pendientes de{' '}
      <span className="adv-num">{report.suggestions}</span>
    </p>
  );
}

/** Pending suggestions and review findings are what a trader looks for first. */
function TabCount({ tabId, report }: { tabId: TabId; report: OptimizationReport }) {
  const count =
    tabId === 'suggestions' ? report.pending : tabId === 'review' ? report.report.review.length : 0;
  if (!count) return null;
  return <span className="adv-tag adv-tag--default adv-num ml-2">{count}</span>;
}

interface TabContentProps {
  tabId: TabId;
  executionId: string;
  report: OptimizationReport;
}

function TabContent({ tabId, executionId, report }: TabContentProps) {
  const analysis = asDeliveryReport(report.report);
  switch (tabId) {
    case 'summary':
      return <SummaryTab report={analysis} />;
    case 'suggestions':
      return <SuggestionsTab executionId={executionId} report={report} />;
    case 'lines':
      return <LinesTab report={analysis} />;
    case 'review':
      return <ReviewTab report={analysis} />;
    case 'history':
      return <AppliedHistoryTab executionId={executionId} lines={analysis.lines} links={dv360Context(analysis)} />;
  }
}

function EmptyState({ executionId }: { executionId: string }) {
  return (
    <section className="adv-card adv-card--featured px-5 py-4 flex items-start gap-4">
      <div className="adv-state__icon w-10 h-10 m-0 flex-shrink-0">
        <Sparkles size={18} />
      </div>
      <div>
        <h2 className="adv-section-title">Optimizaciones</h2>
        <p className="adv-section-desc mt-1">
          Aún no hay informes de optimización para {executionId}. Aparecen aquí cuando alguien ejecuta{' '}
          <code className="adv-num text-adv-primary">/vera:delivery-report</code> para esta ejecución:
          uno por día, con sus sugerencias listas para aprobar.
        </p>
      </div>
    </section>
  );
}

function SectionSkeleton() {
  return (
    <section className="adv-card adv-card--featured px-5 py-4 space-y-3">
      <Skeleton className="h-6 w-48" />
      <Skeleton className="h-4 w-2/3" />
      <Skeleton className="h-8 w-full" />
    </section>
  );
}

function TabSkeleton() {
  return (
    <div className="adv-card adv-card--featured px-5 py-5 space-y-3">
      <Skeleton className="h-5 w-40" />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {Array.from({ length: 4 }).map((_, index) => (
          <Skeleton key={index} className="h-20 w-full" />
        ))}
      </div>
    </div>
  );
}
